import React, { useRef } from 'react';
import { shape, number } from 'prop-types';
import classnames from 'classnames';

import {
  progressBarInitialColor, getProgressBarSize, getProgressBarColor
} from '../../../../utils/promo-presentation-utils';
import { usePromoCart } from '../../../../contexts/PromoCartProvider';
import { usePromoModel } from '../../../../contexts/PromoModelProvider';
import './PromoProgressBarSegment.scss';

export const PromoProgressBarSegment = ({ rewardTier }) => {
  const { progressMade, isPromoAchieved, currentPromoTier } = usePromoCart();
  const { isMsb, isBmsm, rewardTiers } = usePromoModel();
  const wasPromoUnstarted = useRef(currentPromoTier === 0 && !progressMade);
  const wasPromoAchieved = useRef(isPromoAchieved);
  const previousPromoTier = useRef(currentPromoTier);

  const isFirstTier = rewardTier.tier === 0;
  const isLastTier = rewardTier.tier === rewardTiers.length - 1;
  const isPromoInProgress = (currentPromoTier > 0) || (progressMade > 0);

  const width = getProgressBarSize({
    rewardTier,
    currentPromoTier,
    progressMade
  });

  const progressColor = getProgressBarColor({
    rewardTier,
    rewardTiers,
    currentPromoTier,
    isPromoAchieved
  });

  const hasTickMark = isBmsm && (isFirstTier || isLastTier);
  let tickMarkColor = progressBarInitialColor;
  if ((isFirstTier && isPromoInProgress) || (isLastTier && isPromoAchieved)) {
    tickMarkColor = progressColor;
  }

  const isSuppressTickMarkTransition = (
    (isFirstTier && (wasPromoUnstarted.current || currentPromoTier > 0))
    || (isLastTier && !isPromoAchieved));

  const promoProgressSegmentBarClasses = classnames('promo-progress-segment__bar', {
    'promo-progress-segment__bar--msb': isMsb,
    'promo-progress-segment__bar--bmsm': isBmsm,
  });

  const promoProgressTickMarkClasses = classnames('promo-progress-segment__tick-mark', {
    'promo-progress-segment__tick-mark--suppress-transition': isSuppressTickMarkTransition,
  });

  // we animate current tier. In border cases, we animate both above and below.
  const promoProgressSegmentLevelClasses = classnames('promo-progress-segment__level', {
    'promo-progress-segment__level--with-transition':
      rewardTier.tier === currentPromoTier
      || (rewardTier.tier === currentPromoTier - 1 && !progressMade),
  });

  wasPromoUnstarted.current = !isPromoInProgress;
  wasPromoAchieved.current = isPromoAchieved;
  previousPromoTier.current = currentPromoTier;

  return (
    <div className={promoProgressSegmentBarClasses}>
      {hasTickMark && (
        <span
          className={promoProgressTickMarkClasses}
          style={{ backgroundColor: tickMarkColor }}
        />
      )}
      <span
        className={promoProgressSegmentLevelClasses}
        style={{
          width,
          backgroundColor: progressColor,
        }}
        data-testid={`promo-progress-segment__level-tier-${rewardTier.tier}`}
      />
    </div>
  );
};

PromoProgressBarSegment.displayName = 'PromoProgressBarSegment';

PromoProgressBarSegment.propTypes = {
  rewardTier: shape({
    tier: number,
    rewardPercent: number,
    rewardAmountPerOrder: number,
  }).isRequired,
};
